import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Pagination,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  HelpOutline as GuideIcon,
  Search as SearchIcon,
  Close as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Markdown } from '../components/Markdown';

/**
 * Renders CHANGELOG.md — what has changed, newest first.
 *
 * Same shape as the user guide and API docs: markdown in public/, rendered by the shared
 * component, so all three benefit from a fix to the renderer. Deliberately not generated from
 * git history — a commit log says what moved in the code, and this needs to say what is
 * different for someone using the app.
 */
/** Releases per page. Each is a dated `## ` section, so this is entries rather than lines. */
const PER_PAGE = 4;

/**
 * Split the document into its intro and one chunk per dated release.
 *
 * Splitting the **markdown** rather than the rendered output keeps the shared `Markdown`
 * component untouched and means CHANGELOG.md stays a plain file anyone can edit — paging is a
 * presentation choice made here, not a format the document has to know about.
 *
 * The lookahead keeps each `## ` heading with its own section. A `---` immediately before a
 * heading is dropped: it separated entries in one long scroll, and a rule at the top of a page
 * is just a stray line.
 */
const splitReleases = (markdown: string) => {
  const firstHeading = markdown.search(/^## /m);
  if (firstHeading === -1) return { intro: markdown, releases: [] as string[] };

  const intro = markdown.slice(0, firstHeading).replace(/\n---\s*$/, '').trimEnd();
  const releases = markdown
    .slice(firstHeading)
    .split(/\n(?=## )/)
    .map(chunk => chunk.replace(/\n---\s*$/, '').trimEnd())
    .filter(Boolean);

  return { intro, releases };
};

/**
 * Does this release mention everything the reader typed?
 *
 * Every term must appear, in any order and anywhere in the release — heading included, so a
 * version (`1.3.0`) or a date (`2026-09-18`) finds its entry as readily as a word does. AND
 * rather than OR because "instagram permalink" should narrow to the entry about both, which is
 * what someone typing two words is asking for.
 *
 * Matching the **markdown** rather than the rendered text means a term that only appears inside
 * formatting still counts — searching `schedule_variants` finds it even though it renders as a
 * code span.
 */
const matches = (release: string, query: string) => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = release.toLowerCase();
  return terms.every(term => haystack.includes(term));
};

export const Changelog: React.FC = () => {
  const navigate = useNavigate();
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetch('/CHANGELOG.md')
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.text();
      })
      .then(text => {
        // A missing file is served as index.html by the SPA fallback, which would otherwise
        // render as a wall of HTML
        if (text.trimStart().startsWith('<')) throw new Error('not found');
        setSource(text);
      })
      .catch(() => setError('Could not load the changelog.'))
      .finally(() => setLoading(false));
  }, []);

  const { intro, releases } = splitReleases(source);
  const searching = query.trim().length > 0;
  const found = searching ? releases.filter(r => matches(r, query.trim())) : releases;
  const pageCount = Math.max(1, Math.ceil(found.length / PER_PAGE));
  // Guard against a stale page: narrowing a search from 12 matches to 2 while on page 3 would
  // otherwise show an empty card rather than the results.
  const currentPage = Math.min(page, pageCount);
  const visible = found.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">What's Changed</Typography>
          <Typography variant="body2" color="text.secondary">
            Notable changes to ShoutVector, newest first.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<GuideIcon />} onClick={() => navigate('/guide')}>
            User Guide
          </Button>
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            href="/CHANGELOG.md"
            target="_blank"
            rel="noopener noreferrer"
          >
            Raw markdown
          </Button>
        </Box>
      </Box>

      {!error && (
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            // A new search starts at its first result, not wherever the last one was paged to
            setPage(1);
          }}
          placeholder="Search the change history — a feature, a version, a date"
          sx={{
            mb: 3,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => { setQuery(''); setPage(1); }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      )}

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : searching && found.length === 0 ? (
        /* Say what was searched for. "No results" alone leaves the reader wondering whether
           the page is broken or the term genuinely never appears. */
        <Alert severity="info">
          Nothing in the change history mentions <strong>{query.trim()}</strong>. Every word has
          to appear somewhere in the same entry — try one word, or a different spelling.
        </Alert>
      ) : (
        <>
          {searching && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {found.length} of {releases.length} {releases.length === 1 ? 'release' : 'releases'}{' '}
              {found.length === 1 ? 'mentions' : 'mention'} <strong>{query.trim()}</strong>
            </Typography>
          )}
          <Card>
            <CardContent sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
              {/* The intro explains what this page is, so it stays on every page rather than
                  disappearing the moment someone looks at an older release — but it is not a
                  search result, so it steps aside once the list is filtered. */}
              {intro && !searching && <Markdown source={intro} />}
              <Markdown source={visible.join('\n\n---\n\n')} />
            </CardContent>
          </Card>

          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(_, value) => {
                  setPage(value);
                  // Without this, paging from the foot of a long entry leaves you mid-way down
                  // the next one with no idea the page changed.
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
              />
              <Typography variant="body2" color="text.secondary">
                {searching ? `${found.length} matching` : `${releases.length} releases`}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
